/**
 * Componente para reprodução de mensagens de áudio
 * 
 * Player customizado com waveform visual no estilo WhatsApp Web,
 * controles de play/pause, seek por clique nas ondas,
 * duração total e controle de velocidade de reprodução.
 * 
 * Integrado com AudioPlayerContext para garantir que apenas
 * um áudio seja reproduzido por vez em toda a aplicação.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Play, Pause, Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';

interface AudioMessageBubbleProps {
  // URL do áudio (pode ser path relativo do storage ou URL completa)
  url: string;
  // Duração em segundos (opcional, obtido dos metadados)
  duration?: number;
  // Se é mensagem de voz (PTT) ou arquivo de áudio
  isPTT?: boolean;
  // Direção da mensagem para estilização
  direction: 'inbound' | 'outbound';
  // Indica se o áudio foi reproduzido pelo destinatário (status PLAYED da Z-API)
  isAudioPlayed?: boolean;
}

/**
 * Formata segundos para string no formato "M:SS"
 * Exemplo: 65 -> "1:05", 4 -> "0:04"
 */
const formatTime = (seconds: number): string => {
  if (!seconds || isNaN(seconds) || !isFinite(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export function AudioMessageBubble({ 
  url, 
  duration: initialDuration, 
  isPTT = true,
  direction,
  isAudioPlayed = false
}: AudioMessageBubbleProps) {
  // Gera ID único para este player baseado na URL
  const playerId = useMemo(() => `audio-${url}`, [url]);
  
  // Hook do contexto global de áudio para controle de reprodução única
  const { registerAudio, unregisterAudio, setPlaying, currentPlayingId } = useAudioPlayer();
  
  // Verifica se ESTE áudio é o que está tocando atualmente
  const isPlaying = currentPlayingId === playerId;

  // Estados locais do player
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(initialDuration || 0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  
  // Referências DOM
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveformRef = useRef<HTMLDivElement>(null);

  // Converte URL relativa para completa se necessário
  const audioUrl = getStorageUrl(url) || url;

  // Gera 45 barras com alturas pseudo-aleatórias baseadas na duração
  // Usando função seeded para garantir que o mesmo áudio sempre gere o mesmo padrão
  const waveformHeights = useMemo(() => {
    const count = 45; // Número de barras no estilo WhatsApp
    
    // Função para gerar número pseudo-aleatório com seed
    const seededRandom = (seed: number): number => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    
    return Array.from({ length: count }, (_, i) => {
      // Altura entre 40% e 100% para visual mais natural
      const height = seededRandom(duration + i * 123.456) * 0.6 + 0.4;
      return Math.round(height * 100);
    });
  }, [duration]);

  // Calcula progresso da reprodução (0-100)
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Indica se é mensagem enviada (outbound)
  const isOutbound = direction === 'outbound';

  // Registra/desregistra o áudio no contexto global quando monta/desmonta
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      registerAudio(playerId, audio);
    }
    return () => {
      unregisterAudio(playerId);
    };
  }, [playerId, registerAudio, unregisterAudio]);

  // Sincroniza o estado do áudio com o contexto global
  // Se outro áudio começou a tocar, este deve pausar
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    // Se este não é o áudio atual e está tocando, pausa
    if (currentPlayingId !== playerId && !audio.paused) {
      audio.pause();
    }
  }, [currentPlayingId, playerId]);

  // Configura listeners do elemento de áudio
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Prioriza initialDuration (do banco de dados/metadata)
    // Só usa audio.duration como fallback se initialDuration não existe E audio.duration é válido
    const handleLoadedMetadata = () => {
      if (!initialDuration && audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    // Handler para quando o áudio termina naturalmente
    const handleEnded = () => {
      setPlaying(null); // Limpa o estado global
      setCurrentTime(0);
      audio.currentTime = 0;
    };

    const handleError = () => {
      setError(true);
      setIsLoading(false);
      console.error('[AudioMessageBubble] Erro ao carregar áudio:', audioUrl);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    // Adiciona event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);

    // Cleanup dos listeners
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [audioUrl, initialDuration, setPlaying]);

  // Atualiza velocidade de reprodução quando alterada
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  /**
   * Alterna entre play e pause
   * Usa o contexto global para garantir apenas um áudio por vez
   */
  const togglePlay = () => {
    if (!audioRef.current || error) return;

    if (isPlaying) {
      audioRef.current.pause();
      setPlaying(null); // Nenhum áudio tocando
    } else {
      audioRef.current.play();
      setPlaying(playerId); // Define este como o áudio ativo (pausa outros automaticamente)
    }
  };

  /**
   * Manipula clique no waveform para seek
   * Calcula a posição relativa do clique e pula para esse ponto do áudio
   */
  const handleWaveformClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !waveformRef.current || error || isLoading) return;

    const rect = waveformRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  /**
   * Cicla entre as velocidades de reprodução: 1x -> 1.5x -> 2x -> 1x
   */
  const cyclePlaybackRate = () => {
    const rates = [1, 1.5, 2];
    const currentIndex = rates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % rates.length;
    setPlaybackRate(rates[nextIndex]);
  };

  // Estado de erro - mostra mensagem de indisponibilidade
  if (error) {
    return (
      <div className="flex items-center gap-2 py-1">
        <Mic className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Áudio indisponível</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 md:gap-3 min-w-[160px] md:min-w-[200px] max-w-[250px] md:max-w-[300px]">
      {/* Elemento de áudio oculto */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Container do botão Play/Pause com indicador PLAYED posicionado */}
      <div className="relative shrink-0">
        {/* Botão Play/Pause - Cores fixas padrão WhatsApp Web */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 md:h-10 md:w-10 rounded-full hover:opacity-80"
          style={{ backgroundColor: '#54656f', color: '#ffffff' }}
          onClick={togglePlay}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="h-3.5 w-3.5 md:h-4 md:w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-3.5 w-3.5 md:h-4 md:w-4" />
          ) : (
            <Play className="h-3.5 w-3.5 md:h-4 md:w-4 ml-0.5" />
          )}
        </Button>

        {/* Indicador de áudio reproduzido (PLAYED) - bolinha azul estilo WhatsApp */}
        {/* Posicionada no canto inferior direito do botão de play, similar ao status online */}
        {isAudioPlayed && direction === 'outbound' && (
          <div 
            className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2"
            style={{ 
              backgroundColor: '#34b7f1',
              borderColor: 'hsl(var(--chat-bubble-outbound))'
            }}
            title="Áudio reproduzido"
          />
        )}
      </div>

      {/* Área de waveform e controles - Responsivo */}
      <div className="flex-1 min-w-0">
        {/* Waveform - Barras verticais que representam as ondas do áudio */}
        <div
          ref={waveformRef}
          className="flex-1 max-w-[100px] md:max-w-[160px] flex items-center gap-[1px] h-5 md:h-6 cursor-pointer"
          onClick={handleWaveformClick}
        >
          {waveformHeights.map((height, index) => {
            // Calcula se esta barra já foi "tocada" baseado no progresso
            const barProgress = (index / waveformHeights.length) * 100;
            const isPlayed = barProgress <= progress;
            
            // Barra do waveform - Cores fixas padrão WhatsApp Web
            return (
              <div
                key={index}
                className="w-[1.5px] md:w-[2px] rounded-full transition-colors duration-100"
                style={{ 
                  height: `${height}%`,
                  backgroundColor: isPlayed ? '#5fc6c0' : '#9fa8af' 
                }}
              />
            );
          })}
        </div>

        {/* Tempo atual, velocidade e duração total - Cores fixas padrão WhatsApp Web */}
        <div className="flex items-center justify-between mt-1">
          {/* Tempo atual (esquerda) */}
          <span className="text-[10px]" style={{ color: 'hsl(var(--chat-text-secondary))' }}>
            {formatTime(currentTime)}
          </span>
          
          {/* Indicador PTT, velocidade e duração total (direita) */}
          <div className="flex items-center gap-2">
            {/* Ícone de microfone para PTT */}
            {isPTT && (
              <Mic className="h-3 w-3" style={{ color: 'hsl(var(--chat-text-secondary))' }} />
            )}
            
            {/* Botão de velocidade de reprodução */}
            <button
              onClick={cyclePlaybackRate}
              className="text-[10px] font-medium px-1 rounded hover:bg-black/5"
              style={{ color: 'hsl(var(--chat-text-secondary))' }}
            >
              {playbackRate}x
            </button>

            {/* Duração total do áudio */}
            <span className="text-[10px]" style={{ color: 'hsl(var(--chat-text-secondary))' }}>
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
